import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import ChatHeader from "../components/chat/ChatHeader";
import PhaseIndicator from "../components/chat/PhaseIndicator";
import ChatMessages from "../components/chat/ChatMessages";
import ChatInput from "../components/chat/ChatInput";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Hannila's 6-turn protocol: ~12 chat messages alternating user/assistant.
// Map current message count → conceptual phase the user is currently engaging with.
//   Msg 1   : assistant intro + context questions
//   Msg 2   : user answers context (industry, business model, strategic priority) → entering People
//   Msg 3-4 : People questions / answers
//   Msg 5-6 : Process questions / answers
//   Msg 7-8 : Data questions / answers
//   Msg 9-10: Technology questions / answers
//   Msg 11-12: Governance / decision-types questions / answers
//   Msg 13+ : Report emission
const phaseFromMessageCount = (count) => {
  if (count <= 1)  return "welcome";   // first assistant message arrived
  if (count <= 3)  return "people";    // user has provided context, AI asks People
  if (count <= 5)  return "process";
  if (count <= 7)  return "data";
  if (count <= 9)  return "technology";
  if (count <= 11) return "governance";
  return "report";
};

const AssessmentChatPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const [assessment, setAssessment] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [currentPhase, setCurrentPhase] = useState("welcome");

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Auto-focus the input when the AI finishes (initial load done OR after a response arrives)
  useEffect(() => {
    if (!loading && !sending && assessment?.status !== "completed") {
      // Slight delay so scroll finishes first
      const t = setTimeout(() => inputRef.current?.focus(), 120);
      return () => clearTimeout(t);
    }
  }, [loading, sending, messages.length, assessment?.status]);

  const startAssessment = useCallback(async () => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/assessments/${id}/start`);
      setMessages([response.data.message]);
    } catch (err) {
      console.error("Failed to start assessment:", err);
      toast.error("Failed to start assessment");
    }
  }, [id]);

  const fetchAssessment = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/assessments/${id}`);
      setAssessment(response.data);
      setMessages(response.data.chat_history || []);
      // Drive phase from the actual chat history length so the indicator stays
      // in sync with the conversation regardless of what the backend reports.
      const msgs = response.data.chat_history || [];
      if (response.data.status === "completed") {
        setCurrentPhase("report");
      } else {
        setCurrentPhase(phaseFromMessageCount(msgs.length));
      }
      if (!msgs.length) {
        startAssessment();
      }
    } catch (err) {
      console.error("Failed to fetch assessment:", err);
      toast.error("Failed to load assessment");
      navigate("/assessments");
    } finally {
      setLoading(false);
    }
  }, [id, navigate, startAssessment]);

  useEffect(() => {
    fetchAssessment();
  }, [fetchAssessment]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!inputValue.trim() || sending) return;

    const userMessage = inputValue.trim();
    setInputValue("");
    setSending(true);

    const tempUserMsg = { role: "user", content: userMessage, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, tempUserMsg]);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/assessments/${id}/chat`, {
        message: userMessage
      });

      setMessages(prev => [...prev, response.data.message]);

      if (response.data.report_ready) {
        toast.success("Assessment complete! Redirecting to dashboard...");
        setAssessment(prev => ({ ...prev, status: "completed" }));
        setCurrentPhase("report");
        setTimeout(() => navigate("/dashboard"), 3000);
      } else {
        // After this exchange the chat now has 2 more messages
        setCurrentPhase(phaseFromMessageCount(messages.length + 2));
      }
    } catch (err) {
      console.error("Failed to send message:", err);
      toast.error("Failed to send message. Please try again.");
      setMessages(prev => prev.slice(0, -1));
      setInputValue(userMessage);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-pulse-glow w-12 h-12 rounded-full bg-[#ECFEFF] border border-[#67E8F9] flex items-center justify-center">
          <div className="w-6 h-6 rounded-full bg-[#0891B2]" />
        </div>
      </div>
    );
  }

  const isCompleted = assessment?.status === "completed";
  // Hannila's 6-turn protocol: Context + 4 pillars + Governance ≈ 12 total messages
  // (alternating user/assistant). If the user has answered 5 turns (~10 msgs) and
  // is now sending the governance answer, the next assistant response is the full
  // report emission — show the dedicated progress indicator.
  const isFinalTurn = sending && messages.length >= 10;

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <ChatHeader assessment={assessment} assessmentId={id} />
      <PhaseIndicator currentPhase={currentPhase} />
      <ChatMessages
        messages={messages}
        sending={sending}
        isCompleted={isCompleted}
        assessmentId={id}
        messagesEndRef={messagesEndRef}
        isFinalTurn={isFinalTurn}
      />
      <ChatInput
        inputRef={inputRef}
        inputValue={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onSubmit={sendMessage}
        disabled={sending}
        isCompleted={isCompleted}
        assessmentId={id}
      />
    </div>
  );
};

export default AssessmentChatPage;
